import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Save, AutoAwesome, MenuOpen, Menu as MenuIcon } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import StudioCanvas from './canvas/StudioCanvas';
import ProjectSidebar from './components/ProjectSidebar';
import { useStudioStore, StudioAsset } from './stores/studio.store';
import { useCanvasStore } from './canvas/useCanvasStore';
import { projectsService } from '../../services/projects.service';

export default function StudioPage() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();

  const projectId = useStudioStore((s) => s.projectId);
  const projectName = useStudioStore((s) => s.projectName);
  const setProject = useStudioStore((s) => s.setProject);
  const loadAssets = useStudioStore((s) => s.loadAssets);
  const isDirty = useStudioStore((s) => s.isDirty);
  const setDirty = useStudioStore((s) => s.setDirty);
  const assets = useStudioStore((s) => s.assets);
  const activeTool = useStudioStore((s) => s.activeTool);
  const activeAssetId = useStudioStore((s) => s.activeAssetId);
  const pendingVideoOps = useStudioStore((s) => s.pendingVideoOps);

  const canvasNodes = useCanvasStore((s) => s.nodes);
  const canvasEdges = useCanvasStore((s) => s.edges);

  const { enqueueSnackbar } = useSnackbar();
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout>>();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Clean up legacy sessionStorage key (no longer used)
  useEffect(() => {
    try { sessionStorage.removeItem('mx-studio-workspace'); } catch { /* ignore */ }
  }, []);

  // When URL contains a project ID → load it
  const { data: wsData, isLoading: isLoadingWs } = useQuery({
    queryKey: ['studio-project', urlProjectId],
    queryFn: () => projectsService.get(urlProjectId!),
    enabled: !!urlProjectId,
  });

  useEffect(() => {
    if (!urlProjectId) {
      // No project in URL — clear state
      if (projectId) {
        setProject(null, null);
        useStudioStore.getState().clearAssets();
        useCanvasStore.setState({ nodes: [], edges: [] });
      }
      return;
    }

    if (wsData) {
      const ws = wsData;
      setProject(ws.id, ws.name);

      // Load canvas state if present
      if (ws.canvasState?.nodes && ws.canvasState?.edges) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        useCanvasStore.setState({ nodes: ws.canvasState.nodes as any, edges: ws.canvasState.edges as any });
      } else {
        useCanvasStore.setState({ nodes: [], edges: [] });
      }

      // Load assets
      if (ws.assets?.length) {
        loadAssets(
          ws.assets.map((a: any) => ({
            id: a.id,
            type: a.type,
            data: a.signed_url || a.data,
            mimeType: a.mime_type,
            prompt: a.prompt,
            description: a.description,
            tool: a.tool || 'generate',
            createdAt: new Date(a.created_at || a.createdAt || Date.now()).getTime(),
            serverId: a.id,
            gcsPath: a.gcs_path,
          }))
        );
      } else {
        useStudioStore.getState().clearAssets();
      }
      setDirty(false);
    }
  }, [urlProjectId, wsData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save canvas state to project (debounced)
  useEffect(() => {
    if (!projectId || canvasNodes.length === 0) return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      projectsService.update(projectId, {
        canvasState: { nodes: canvasNodes, edges: canvasEdges },
      }).catch(() => {/* silent */});
    }, 3000);
    return () => clearTimeout(autoSaveTimer.current);
  }, [projectId, canvasNodes, canvasEdges]);

  // Manual save
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!projectId) throw new Error('No project');
      const { nodes, edges } = useCanvasStore.getState();
      await projectsService.update(projectId, {
        activeTool,
        activeAssetId,
        pendingVideoOps,
        canvasState: { nodes, edges },
      });
      const newAssets = assets.filter((a: StudioAsset) => !a.serverId);
      for (const asset of newAssets) {
        await projectsService.addAsset(projectId, {
          type: asset.type,
          data: asset.data,
          mimeType: asset.mimeType,
          prompt: asset.prompt,
          description: asset.description,
          tool: asset.tool,
        });
      }
      return newAssets.length;
    },
    onSuccess: () => {
      setDirty(false);
      enqueueSnackbar('Project saved', { variant: 'success' });
    },
    onError: (err: Error) => enqueueSnackbar(err.message, { variant: 'error' }),
  });

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Collapsible sidebar */}
      <ProjectSidebar collapsed={sidebarCollapsed} />

      {/* Main area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* Top bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
            minHeight: 40,
            zIndex: 10,
          }}
        >
          <Tooltip title={sidebarCollapsed ? 'Show projects' : 'Hide projects'}>
            <IconButton size="small" onClick={() => setSidebarCollapsed((c) => !c)}>
              {sidebarCollapsed ? <MenuIcon fontSize="small" /> : <MenuOpen fontSize="small" />}
            </IconButton>
          </Tooltip>
          {projectId && (
            <>
              <Typography variant="body2" fontWeight={600} noWrap>
                {projectName}
              </Typography>
              {isDirty && (
                <Chip label="unsaved" size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
              )}
            </>
          )}
          <Box sx={{ flex: 1 }} />
          {projectId && (
            <Tooltip title="Save project (Ctrl+S)">
              <span>
                <IconButton
                  size="small"
                  onClick={() => saveMutation.mutate()}
                  disabled={!isDirty || saveMutation.isPending}
                >
                  <Save fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>

        {/* Content */}
        {isLoadingWs && urlProjectId ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : projectId ? (
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <StudioCanvas />
          </Box>
        ) : (
          /* Empty state — no project selected */
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              color: 'text.secondary',
            }}
          >
            <AutoAwesome sx={{ fontSize: 56, opacity: 0.3 }} />
            <Typography variant="h6" fontWeight={500} color="text.secondary">
              Select or create a project
            </Typography>
            <Typography variant="body2" color="text.disabled" maxWidth={360} textAlign="center">
              Projects store your creative assets, video generations, and canvas layouts.
              Pick one from the sidebar or create a new one.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
